import { ReactNode, Suspense, useEffect, useRef, useState } from "react";

interface DeferredSectionProps {
  children: ReactNode;
  /** Reserved vertical space before the section mounts, to avoid layout shift. */
  minHeight?: number;
  /** Distance (px) before the viewport at which the section starts loading. */
  rootMargin?: string;
}

/**
 * Mounts its children only when the placeholder approaches the viewport.
 * Combined with React.lazy this keeps below-the-fold code out of the
 * initial JavaScript download.
 */
export const DeferredSection = ({
  children,
  minHeight = 400,
  rootMargin = "600px",
}: DeferredSectionProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (visible) return;
    const el = ref.current;
    if (!el) return;

    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [visible, rootMargin]);

  return (
    <div ref={ref} style={visible ? undefined : { minHeight }}>
      {visible ? <Suspense fallback={<div style={{ minHeight }} />}>{children}</Suspense> : null}
    </div>
  );
};
